import React, { useState, useCallback, useImperativeHandle } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { CollapsiblePanelIcon } from '../../icons';
import { getFuelColor } from '../../../utils/fuelColors';
import VoFuelTechSublist from './VoFuelTechSublist';
import { hasExpandableVoSubfuels } from './generationProfileInputsUtils';

const FUEL_COLOR_KEY_MAP = {
  nuclear: 'nuclear',
  solar: 'solar',
  windOn: 'windon',
  windOff: 'windoff',
  hydro: 'hydro',
  geo: 'geothermal',
  bio: 'bioenergy',
  coal: 'coal',
  gas: 'gas',
  oil: 'oil'
};

/** Shared metrics cards — desktop sidebar + inline block on small viewports */
export function RightPanelMetricsCards({
  balanceMetrics,
  fuelUtilization,
  voGenByFuel = null,
  storageCapacityGW,
  pumpedStorageCapacityGW,
  nuclearAvgGW,
  nuclearPeakGW,
  solarAvgGW,
  solarPeakGW,
  windAvgGW,
  windPeakGW,
  hydroAvgGW,
  hydroPeakGW,
  onMaxShortageClick,
  maxShortageNavIndex = 0,
  maxShortageEventCount = 0,
  displayedMaxShortageTimestamp = null,
  displayedMaxShortageTimestampMs = null,
  onMaxShortageNavPrev = null,
  onMaxShortageNavNext = null,
  systemBalanceHeaderStart
}) {
  /** VO sub-tech CF rows: collapsed by default (same as capacity inputs). */
  const [voFuelExpanded, setVoFuelExpanded] = useState({});

  const toggleVoFuelExpanded = useCallback((fuelKey) => {
    setVoFuelExpanded((prev) => ({ ...prev, [fuelKey]: !prev[fuelKey] }));
  }, []);

  return (
    <>
      <div className="bg-white rounded-lg shadow-card p-3 sm:p-3 hover:shadow-card-hover transition-shadow">
        <div
          className={`flex items-center gap-2 mb-3 min-h-10 py-1 ${
            systemBalanceHeaderStart ? 'justify-between' : ''
          }`}
        >
          {systemBalanceHeaderStart}
          <h3 className="card-header-figma leading-normal">System Balance</h3>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-start">
            <span className="label-figma">Total Shortage</span>
            <div className="text-right">
              {balanceMetrics ? (
                <>
                  <div className="font-mono text-gray-900">{balanceMetrics.totalShortage_TWh.toFixed(1)} TWh</div>
                  <div className="text-xs text-gray-500">({balanceMetrics.shortagePercent.toFixed(1)}%)</div>
                </>
              ) : (
                <span className="font-mono text-gray-400">—</span>
              )}
            </div>
          </div>
          <div className="flex justify-between items-start">
            <span className="label-figma">Max Shortage</span>
            <div className="text-right">
              {balanceMetrics ? (
                <>
                  <div className="font-mono text-gray-900">{balanceMetrics.maxShortage_GW.toFixed(1)} GW</div>
                  {displayedMaxShortageTimestamp && (
                    <div className="flex items-center justify-end gap-0.5 mt-0.5">
                      {maxShortageEventCount > 1 ? (
                        <button
                          type="button"
                          onClick={onMaxShortageNavPrev}
                          disabled={maxShortageNavIndex <= 0}
                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:pointer-events-none disabled:hover:text-gray-400 disabled:hover:bg-transparent"
                          title="Previous max shortage"
                          aria-label="Previous max shortage"
                        >
                          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={onMaxShortageClick}
                        className="text-xs text-blue-600 hover:text-blue-700 hover:underline disabled:text-gray-500 disabled:no-underline disabled:cursor-default px-0.5"
                        disabled={displayedMaxShortageTimestampMs == null}
                        title="Show this shortage period on the hourly chart"
                      >
                        {displayedMaxShortageTimestamp}
                      </button>
                      {maxShortageEventCount > 1 ? (
                        <button
                          type="button"
                          onClick={onMaxShortageNavNext}
                          disabled={maxShortageNavIndex >= maxShortageEventCount - 1}
                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:pointer-events-none disabled:hover:text-gray-400 disabled:hover:bg-transparent"
                          title="Next max shortage"
                          aria-label="Next max shortage"
                        >
                          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                        </button>
                      ) : null}
                    </div>
                  )}
                </>
              ) : (
                <span className="font-mono text-gray-400">—</span>
              )}
            </div>
          </div>
          <div className="flex justify-between items-start">
            <span className="label-figma">Curtailed</span>
            {balanceMetrics ? (
              <span className="font-mono text-gray-900">
                {balanceMetrics.totalSurplus_TWh.toFixed(1)} TWh{' '}
                <span className="text-xs text-gray-500 font-sans">
                  ({balanceMetrics.curtailmentPercent.toFixed(1)}%)
                </span>
              </span>
            ) : (
              <span className="font-mono text-gray-400">—</span>
            )}
          </div>
          {balanceMetrics?.hoursUnserved != null && (
            <div className="flex justify-between items-start">
              <span className="label-figma">Hours Unserved</span>
              <span className="font-mono text-gray-900">{balanceMetrics.hoursUnserved}</span>
            </div>
          )}
          {((storageCapacityGW || 0) > 0 || (pumpedStorageCapacityGW || 0) > 0) && balanceMetrics && (
            <>
              {(storageCapacityGW || 0) > 0 && (
                <>
                  <div className="border-t pt-2 mt-2 flex justify-between items-start">
                    <span className="label-figma">Battery charged</span>
                    <span className="font-mono text-gray-900">{balanceMetrics.storageChargeTWh.toFixed(2)} TWh</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="label-figma">Battery discharged</span>
                    <span className="font-mono text-gray-900">{balanceMetrics.storageDischargeTWh.toFixed(2)} TWh</span>
                  </div>
                </>
              )}
              {(pumpedStorageCapacityGW || 0) > 0 && (
                <>
                  <div
                    className={`flex justify-between items-start ${(storageCapacityGW || 0) > 0 ? 'pt-2' : 'border-t pt-2 mt-2'}`}
                  >
                    <span className="label-figma">Pumped charged</span>
                    <span className="font-mono text-gray-900">{balanceMetrics.pumpedStorageChargeTWh.toFixed(2)} TWh</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="label-figma">Pumped discharged</span>
                    <span className="font-mono text-gray-900">{balanceMetrics.pumpedStorageDischargeTWh.toFixed(2)} TWh</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg sm:rounded-xl shadow-card p-3 sm:p-3 hover:shadow-card-hover transition-shadow">
        {fuelUtilization && Object.keys(fuelUtilization).length > 0 && (
          <>
            <div>
              <h3 className="card-header-figma">Capacity Factors</h3>
              <div className="pt-2 space-y-2 text-sm">
                {Object.entries(fuelUtilization).map(([key, fuel]) => {
                  const pct = (fuel.utilization * 100).toFixed(1);
                  const subRows = voGenByFuel?.[key];
                  const expandable = hasExpandableVoSubfuels(subRows);
                  const expanded = Boolean(voFuelExpanded[key]);
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {expandable ? (
                            <button
                              type="button"
                              onClick={() => toggleVoFuelExpanded(key)}
                              className="p-0.5 -ml-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                              aria-expanded={expanded}
                              aria-label={
                                expanded
                                  ? `Collapse ${fuel.label} sub-fuels`
                                  : `Expand ${fuel.label} sub-fuels`
                              }
                            >
                              {expanded ? (
                                <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                              ) : (
                                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                              )}
                            </button>
                          ) : null}
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: getFuelColor(FUEL_COLOR_KEY_MAP[key] || key) }}
                          />
                          <span className="label-figma truncate">{fuel.label}</span>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <span className="font-mono text-gray-900">{pct}%</span>
                          <span className="font-mono text-gray-500 ml-1 text-xs">{fuel.generationTWh.toFixed(1)} TWh</span>
                        </div>
                      </div>
                      {expandable && expanded ? (
                        <VoFuelTechSublist rows={subRows} mode="generation" />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="border-t border-gray-200 my-3" />
          </>
        )}
        <div>
          <h3 className="card-header-figma">Delivered GW</h3>
          <div className="pt-2 space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="label-figma">Nuclear Avg</span>
              <span className="font-mono text-gray-900">{nuclearAvgGW !== null ? nuclearAvgGW.toFixed(2) : '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="label-figma">Nuclear Peak</span>
              <span className="font-mono text-gray-900">{nuclearPeakGW !== null ? nuclearPeakGW.toFixed(2) : '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="label-figma">Solar Avg</span>
              <span className="font-mono text-gray-900">{solarAvgGW !== null ? solarAvgGW.toFixed(2) : '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="label-figma">Solar Peak</span>
              <span className="font-mono text-gray-900">{solarPeakGW !== null ? solarPeakGW.toFixed(2) : '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="label-figma">Wind Avg</span>
              <span className="font-mono text-gray-900">{windAvgGW !== null ? windAvgGW.toFixed(2) : '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="label-figma">Wind Peak</span>
              <span className="font-mono text-gray-900">{windPeakGW !== null ? windPeakGW.toFixed(2) : '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="label-figma">Hydro Avg</span>
              <span className="font-mono text-gray-900">{hydroAvgGW !== null ? hydroAvgGW.toFixed(2) : '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="label-figma">Hydro Peak</span>
              <span className="font-mono text-gray-900">{hydroPeakGW !== null ? hydroPeakGW.toFixed(2) : '—'}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const GenerationProfileRightPanelInner = React.forwardRef(function GenerationProfileRightPanelInner(
  {
    onLayoutChange,
    balanceMetrics,
    fuelUtilization,
    voGenByFuel = null,
    storageCapacityGW,
    pumpedStorageCapacityGW,
    nuclearAvgGW,
    nuclearPeakGW,
    solarAvgGW,
    solarPeakGW,
    windAvgGW,
    windPeakGW,
    hydroAvgGW,
    hydroPeakGW,
    onMaxShortageClick,
    maxShortageNavIndex,
    maxShortageEventCount,
    displayedMaxShortageTimestamp,
    displayedMaxShortageTimestampMs,
    onMaxShortageNavPrev,
    onMaxShortageNavNext
  },
  ref
) {
  const [expanded, setExpanded] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia('(max-width: 1023px)').matches
  );

  const notifyAfterPaint = useCallback(() => {
    requestAnimationFrame(() => {
      onLayoutChange?.();
    });
  }, [onLayoutChange]);

  const collapse = useCallback(() => {
    setExpanded(false);
    notifyAfterPaint();
  }, [notifyAfterPaint]);

  const expand = useCallback(() => {
    setExpanded(true);
    notifyAfterPaint();
  }, [notifyAfterPaint]);

  useImperativeHandle(ref, () => ({ collapse, expand, isExpanded: () => expanded }), [
    collapse,
    expand,
    expanded,
  ]);

  const metricsProps = {
    balanceMetrics,
    fuelUtilization,
    voGenByFuel,
    storageCapacityGW,
    pumpedStorageCapacityGW,
    nuclearAvgGW,
    nuclearPeakGW,
    solarAvgGW,
    solarPeakGW,
    windAvgGW,
    windPeakGW,
    hydroAvgGW,
    hydroPeakGW,
    onMaxShortageClick,
    maxShortageNavIndex,
    maxShortageEventCount,
    displayedMaxShortageTimestamp,
    displayedMaxShortageTimestampMs,
    onMaxShortageNavPrev,
    onMaxShortageNavNext
  };

  return (
    <div
      className={`hidden lg:flex flex-shrink-0 flex-col overflow-hidden transition-none rounded-r-lg ${
        expanded ? 'w-[232px] xl:w-[272px]' : 'w-14 min-w-[3.5rem]'
      } ${!expanded ? 'bg-white' : ''}`}
    >
      {!expanded && (
        <button
          type="button"
          onClick={expand}
          className="btn-panel-toggle-bar"
          title="Expand panel"
          aria-label="Expand panel"
        >
          <CollapsiblePanelIcon size={18} className="rotate-180" />
        </button>
      )}
      <div
        className={`flex-1 overflow-y-auto pt-0 space-y-2 flex flex-col min-h-0 ${expanded ? '' : 'hidden'}`}
        aria-hidden={!expanded}
      >
        <RightPanelMetricsCards
          {...metricsProps}
          systemBalanceHeaderStart={
            <button
              type="button"
              onClick={collapse}
              className="btn-panel-toggle-sm"
              title="Collapse panel"
              aria-label="Collapse panel"
            >
              <CollapsiblePanelIcon size={18} />
            </button>
          }
        />
      </div>
    </div>
  );
});

const GenerationProfileRightPanel = React.memo(GenerationProfileRightPanelInner);
GenerationProfileRightPanel.displayName = 'GenerationProfileRightPanel';

export default GenerationProfileRightPanel;
